import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { queriesRecurringInvoices } from "@query";
import { WSQueries } from "../../query/WSQuery";
import { getLocalStartDayByUTC } from "../../utils/dates";
import { InvoicesForm } from "../components/InvoicesForm/InvoicesForm";
import { useDefaultInvoiceSubject } from "../utils";
import {
  attachmentsToFormData,
  convertToLineItemFormDataFromRecurring,
  creditFeeToFormDataFromV3,
  frequencyToFormDataFromV3,
  lateFeeToFormDataFromV3,
  paymentMethodsToFormDataFromV3,
  recurringSplitsToFormData
} from "../utils/invoicesFormUtils";

type Props = RouteComponentProps<{ invoiceTemplateId: string }>;

export const InvoicesDuplicateInvoiceTemplate: React.FC<Props> = props => {
  const recurringInvoiceId = props.match.params.invoiceTemplateId;
  const queryRecurringInvoice =
    queriesRecurringInvoices.getRecurringInvoice.useQuery({
      recurringInvoiceId
    });

  const defaultSubject = useDefaultInvoiceSubject();

  return (
    <WSQueries queries={{ queryRecurringInvoice }}>
      {({ queryRecurringInvoice: { data: recurringInvoice } }) => {
        const invoiceTemplate = recurringInvoice.invoiceTemplate;
        const scheduledDate =
          recurringInvoice.schedule?.scheduleDates?.[0]?.date ??
          recurringInvoice.nextOccurrenceDate;
        const sourceSendDate = scheduledDate
          ? getLocalStartDayByUTC(scheduledDate)
          : undefined;

        // The source's first occurrence is normally in the past, and the form
        // will not accept a send date that is — carrying it over left "Create
        // series" rejecting a form that looked complete (PLA-3693).
        const sendDate =
          sourceSendDate && sourceSendDate > new Date()
            ? sourceSendDate
            : new Date();

        const dueDate = new Date(sendDate);
        dueDate.setDate(
          dueDate.getDate() + (invoiceTemplate?.paymentTermsDays ?? 0)
        );
        const isDueInFuture = dueDate > new Date();

        return (
          <InvoicesForm
            defaultValues={{
              subject: String(
                recurringInvoice.metadata?.subject || defaultSubject
              ),
              payer: {
                payerId: recurringInvoice.payerId || null,
                payerEngagementId: recurringInvoice.payerEngagementId || null,
                emailsCC: []
              },
              purchaseOrderNumber:
                recurringInvoice.metadata?.purchaseOrderNumber || "",
              lineItems: (invoiceTemplate?.lineItems || []).map(
                convertToLineItemFormDataFromRecurring
              ),
              other: {
                projectName: String(
                  recurringInvoice.metadata?.projectName || ""
                ),
                notes: invoiceTemplate?.notes || "",
                attachments: attachmentsToFormData(
                  invoiceTemplate?.customAttachmentIds
                )
              },
              due: isDueInFuture ? "custom" : "on-receipt",
              customDueDate: isDueInFuture ? dueDate : new Date(),
              includesLateFee:
                !!invoiceTemplate?.lateFeeHandling &&
                !!invoiceTemplate.lateFeeHandling.frequency,
              lateFee: lateFeeToFormDataFromV3(
                invoiceTemplate?.lateFeeHandling
              ),
              send: {
                type: "date",
                date: sendDate
              },
              recurring: !!recurringInvoice.schedule?.frequency,
              frequency: frequencyToFormDataFromV3(recurringInvoice.schedule),
              splits: recurringSplitsToFormData(invoiceTemplate?.splits),
              paymentMethods: paymentMethodsToFormDataFromV3(
                invoiceTemplate?.acceptedPaymentMethods
              ),
              advanced: {
                sendEmails:
                  !!invoiceTemplate?.notificationPreferences?.shouldSendInvoice,
                sendReminders:
                  !!invoiceTemplate?.notificationPreferences
                    ?.shouldSendReminders,
                creditFeeHandling: creditFeeToFormDataFromV3(
                  invoiceTemplate?.creditFeeHandling
                ),
                date: new Date()
              }
            }}
          />
        );
      }}
    </WSQueries>
  );
};
